// PB-4 — usePowerballWeek hook.
//
// Reads the current week's dual POWERBALL slip data via get_pb_current_week RPC.
// Returns both PB10 and MEGA20 slips with selections.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { parsePowerballSlip as parseSlip } from "./publication.ts";
import { dayOfYearInAccra } from "../predictions/sessionDate.ts";
import { getBrowserClient } from "../supabase/browser.ts";

// ─── Types ────────────────────────────────────────────────

export interface PbSelection {
  settlement_state?: string | null;
  settlement_reason?: string | null;
  id: string;
  match_name: string;
  league: string;
  league_short: string | null;
  country_code: string | null;
  kickoff_at: string | null;
  market: string;
  pick: string;
  odds: number;
  layer: "foundation" | "mid_layer" | "amplifier" | "anchor" | "bridge" | "stretch";
  form_score: number | null;
  value_edge_pct: number | null;
  model_probability: number | null;
  best_platform: string | null;
  best_odds: number | null;
  outcome: "win" | "loss" | "void" | null;
}

export interface PbSlip {
  validation_state?: string | null;
  execution_eligible?: boolean;
  fixture_window_start?: string | null;
  fixture_window_end?: string | null;
  contract_version?: number | null;
  publication_mode?: string | null;
  settlement_state?: string | null;
  realized_return?: number | null;
  void_adjusted_odds?: number | null;
  id: string;
  publication_id?: string | null;
  publication_version?: number | null;
  session_date?: string | null;
  original_published_at?: string | null;
  latest_published_at?: string | null;
  slip_type:
    | "powerball_10"
    | "mega_powerball_20"
    | "mixed_mega_jackpot"
    | "lower_lines"
    | "goals_bank"
    | "big_stage"
    | "multiverse";
  combined_odds: number;
  stake_tier: "standard" | "conviction" | "full_send";
  stake_amount: number;
  potential_win: number;
  confidence_score: number | null;
  status: "building" | "assembling" | "assembled" | "published" | "settled" | "rebalance_required";
  result: string | null;
  deduplication_passed: boolean | null;
  published_at: string | null;
  selections: PbSelection[];
}

export interface PbDualWeek {
  id: string;
  week_number: number;
  season: string;
  combined_stake: number;
  deduplication_passed: boolean | null;
  published_at: string | null;
}

export interface PbCore { id: string; source_publication_id: string; source_version: number; label: string; combined_odds: number; selections: PbSelection[] }

export interface UsePowerballWeekState {
  cores?: readonly PbCore[];
  tierContracts?: readonly {tier:string;version:number;target_legs:number;max_legs:number;session_kind:string}[];
  reviewPendingTiers?: readonly string[];
  weekNumber: number;
  dualWeek: PbDualWeek | null;
  pb10: PbSlip | null;
  mega20: PbSlip | null;
  /** MM-UI-2 — MIXED MEGA jackpot slip (45 legs). Sibling
   *  product to PB10/MEGA20. May be null on weeks the assembler couldn't
   *  reach the band (substrate too thin or selection logic gap). */
  mixedMega: PbSlip | null;
  lowerLines: PbSlip | null;
  goalsBank: PbSlip | null;
  bigStage: PbSlip | null;
  multiverse: PbSlip | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

// ─── Parsers ──────────────────────────────────────────────

// ─── Hook ─────────────────────────────────────────────────

export function usePowerballWeek(targetWeek?: number | null, options?: { sessionDate?: string | null; publicationId?: string | null }): UsePowerballWeekState {
  const { user } = useAuthSession();
  const sessionDate = options?.sessionDate ?? null;
  const publicationId = options?.publicationId ?? null;
  const [cores, setCores] = useState<readonly PbCore[]>([]);
  const [tierContracts, setTierContracts] = useState<NonNullable<UsePowerballWeekState["tierContracts"]>>([]);
  const [reviewPendingTiers, setReviewPendingTiers] = useState<readonly string[]>([]);
  const [weekNumber, setWeekNumber] = useState(0);
  const [dualWeek, setDualWeek] = useState<PbDualWeek | null>(null);
  const [pb10, setPb10] = useState<PbSlip | null>(null);
  const [mega20, setMega20] = useState<PbSlip | null>(null);
  const [mixedMega, setMixedMega] = useState<PbSlip | null>(null);
  const [lowerLines, setLowerLines] = useState<PbSlip | null>(null);
  const [goalsBank, setGoalsBank] = useState<PbSlip | null>(null);
  const [bigStage, setBigStage] = useState<PbSlip | null>(null);
  const [multiverse, setMultiverse] = useState<PbSlip | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const hasLoaded = useRef(false);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!user || targetWeek != null || sessionDate || publicationId) return;
    const timer = window.setInterval(refetch, 60_000);
    return () => window.clearInterval(timer);
  }, [refetch, targetWeek, sessionDate, publicationId, user]);

  useEffect(() => {
    if (!user) {
      hasLoaded.current = false;
      setCores([]);setTierContracts([]);setReviewPendingTiers([]);
      setDualWeek(null);
      setPb10(null);
      setMega20(null);
      setMixedMega(null);
      setLowerLines(null);
      setGoalsBank(null);
      setBigStage(null);
      setMultiverse(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const client = getBrowserClient();
    if (!hasLoaded.current) setLoading(true);
    setError(null);

    void (async () => {
      try {
        const useTargetWeek = typeof targetWeek === "number" && Number.isFinite(targetWeek);
        const rpcName = sessionDate || publicationId ? "get_pb_publication_board" : useTargetWeek ? "get_pb_week" : "get_pb_current_week";
        const rpcArgs = sessionDate || publicationId
          ? { ...(sessionDate ? { p_session_date: sessionDate } : {}), ...(publicationId ? { p_publication_id: publicationId } : {}) }
          : useTargetWeek ? { p_week: targetWeek } : undefined;
        const { data, error: rpcError } = await (
          client.rpc as unknown as (
            fn: string,
            args?: Record<string, unknown>,
          ) => Promise<{
            data: Record<string, unknown> | null;
            error: { message: string } | null;
          }>
        ).call(client, rpcName, rpcArgs);

        if (cancelled) return;

        if (rpcError) {
          setError(rpcError.message);
          setLoading(false);
          hasLoaded.current = true;
          return;
        }

        if (!data) {
          setDualWeek(null);
          setPb10(null);
          setMega20(null);
          setMixedMega(null);
          setLowerLines(null);
          setGoalsBank(null);
          setBigStage(null);
          setMultiverse(null);
          setLoading(false);
          hasLoaded.current = true;
          return;
        }

        const raw = data as Record<string, unknown>;
        if (typeof raw.error === "string") throw new Error(raw.error);
        const responseWeek = Number(raw.week_number ?? 0);
        const currentDay = dayOfYearInAccra();
        if (!useTargetWeek && !sessionDate && !publicationId && currentDay !== null && responseWeek !== currentDay) {
          setWeekNumber(currentDay);
          setDualWeek(null);
          setPb10(null);
          setMega20(null);
          setMixedMega(null);
          setLowerLines(null);
          setGoalsBank(null);
          setBigStage(null);
          setMultiverse(null);
          setLoading(false);
          hasLoaded.current = true;
          return;
        }
        setCores(Array.isArray(raw.cores) ? (raw.cores as Record<string, unknown>[]).map(c => ({ ...c, selections: parseSlip(c)?.selections ?? [] })) as PbCore[] : []);
        setWeekNumber(responseWeek);
        setTierContracts(Array.isArray(raw.tier_contracts) ? raw.tier_contracts as NonNullable<UsePowerballWeekState["tierContracts"]> : []);
        setReviewPendingTiers(Array.isArray(raw.validation_attempts) ? (raw.validation_attempts as Record<string, unknown>[]).filter(x => x.state === "REVIEW_PENDING").map(x => String(x.tier)) : []);

        if (raw.dual_week && typeof raw.dual_week === "object") {
          const dw = raw.dual_week as Record<string, unknown>;
          setDualWeek({
            id: String(dw.id ?? ""),
            week_number: Number(dw.week_number ?? 0),
            season: String(dw.season ?? ""),
            combined_stake: Number(dw.combined_stake ?? 0),
            deduplication_passed:
              typeof dw.deduplication_passed === "boolean" ? dw.deduplication_passed : null,
            published_at: typeof dw.published_at === "string" ? dw.published_at : null,
          });
        } else {
          setDualWeek(null);
        }

        if (raw.powerball_10 && typeof raw.powerball_10 === "object") {
          setPb10(parseSlip(raw.powerball_10 as Record<string, unknown>));
        } else {
          setPb10(null);
        }

        if (raw.mega_20 && typeof raw.mega_20 === "object") {
          setMega20(parseSlip(raw.mega_20 as Record<string, unknown>));
        } else {
          setMega20(null);
        }

        if (raw.mixed_mega_jackpot && typeof raw.mixed_mega_jackpot === "object") {
          setMixedMega(parseSlip(raw.mixed_mega_jackpot as Record<string, unknown>));
        } else {
          setMixedMega(null);
        }

        if (raw.lower_lines && typeof raw.lower_lines === "object") {
          setLowerLines(parseSlip(raw.lower_lines as Record<string, unknown>));
        } else {
          setLowerLines(null);
        }

        if (raw.goals_bank && typeof raw.goals_bank === "object") {
          setGoalsBank(parseSlip(raw.goals_bank as Record<string, unknown>));
        } else {
          setGoalsBank(null);
        }

        if (raw.big_stage && typeof raw.big_stage === "object") {
          setBigStage(parseSlip(raw.big_stage as Record<string, unknown>));
        } else {
          setBigStage(null);
        }

        if (raw.multiverse && typeof raw.multiverse === "object") {
          setMultiverse(parseSlip(raw.multiverse as Record<string, unknown>));
        } else {
          setMultiverse(null);
        }

        setLoading(false);
        hasLoaded.current = true;
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
        hasLoaded.current = true;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, tick, targetWeek, sessionDate, publicationId]);

  return useMemo(
    () => ({
      weekNumber,
      cores,
    tierContracts,reviewPendingTiers,
      dualWeek,
      pb10,
      mega20,
      mixedMega,
      lowerLines,
      goalsBank,
      bigStage,
      multiverse,
      loading,
      error,
      refetch,
    }),
    [weekNumber, cores, tierContracts,reviewPendingTiers,dualWeek, pb10, mega20, mixedMega, lowerLines, goalsBank, bigStage, multiverse, loading, error, refetch],
  );
}
